// src/modules/app-views/_apps/appmulti/plans/pages/Init/modals/PlansParametersModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Modal, Tabs, Form, Row, Col, Button, Input, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const COVERAGE_TYPE_OPTIONS = [
  { label: 'money', value: 'money' },
  { label: 'switch', value: 'switch' },
  { label: 'text', value: 'text' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PlansParametersModal = ({ show, setShow, afterClose, initialTab, resetInitialTab }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [coveragesForm] = Form.useForm();
  const [assistsForm] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const defaultTab = 'coverages';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(initialTab || defaultTab);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (show && initialTab) {
      setActiveTab(initialTab);
    }
  }, [show, initialTab]);

  useEffect(() => {
    if (show) {
      const prefs = appService.getModulePreferences({ category: 'plans' }) || {};
      coveragesForm.setFieldsValue({ coverages: prefs.coverages || [] });
      assistsForm.setFieldsValue({ assists: prefs.assists || [] });
    }
  }, [show, coveragesForm, assistsForm]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (!loading) setShow(false);
  }, [loading, setShow]);

  const handleAfterClose = useCallback(() => {
    setActiveTab(defaultTab);
    setLoading(false);
    resetInitialTab?.();
    afterClose?.();
  }, [afterClose, resetInitialTab]);

  const handleTabChange = useCallback(key => {
    setActiveTab(key);
  }, []);

  const handleSaveCoverages = useCallback(async () => {
    try {
      setLoading(true);
      const values = await coveragesForm.validateFields();
      const coverages = (values.coverages || []).map(c => ({
        key: c.key || '',
        label: c.label || '',
        type: c.type || 'money',
      }));
      await appService.updateModulePreferences({
        category: 'plans',
        data: { coverages },
      });
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch {
      // validation errors
    } finally {
      setLoading(false);
    }
  }, [coveragesForm]);

  const handleSaveAssists = useCallback(async () => {
    try {
      setLoading(true);
      const values = await assistsForm.validateFields();
      const assists = (values.assists || []).map(a => ({
        key: a.key || '',
        text: a.text || '',
      }));
      await appService.updateModulePreferences({
        category: 'plans',
        data: { assists },
      });
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch {
      // validation errors
    } finally {
      setLoading(false);
    }
  }, [assistsForm]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'coverages',
      label: translateX('coverages'),
      children: (
        <CoveragesTabContent
          form={coveragesForm}
          loading={loading}
          onSave={handleSaveCoverages}
        />
      ),
    },
    {
      key: 'assists',
      label: translateX('assists'),
      children: (
        <AssistsTabContent
          form={assistsForm}
          loading={loading}
          onSave={handleSaveAssists}
        />
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX('plans_parametrization')}
        </span>
      }
      open={show}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      destroyOnHidden
      width={540}
      footer={null}
      mask={{ closable: !loading }}
      styles={{
        body: {
          maxHeight: 'calc(80vh - 110px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      style={{ top: '10vh' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="parameters-modal-tabs"
      />
    </Modal>
  );
};

// *******************************************************************************
// SUB-COMPONENTS
// *******************************************************************************
const CoveragesTabContent = ({ form, loading, onSave }) => (
  <div style={{ padding: '8px 0', overflowY: 'auto', maxHeight: 'calc(80vh - 240px)' }}>
    <Form form={form} layout="vertical">
      <Form.List name="coverages">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row gutter={8} key={key} align="middle">
                <Col span={7}>
                  <Form.Item
                    {...restField}
                    name={[name, 'key']}
                    rules={[{ required: true, message: '' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input placeholder={translateX('key')} />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item
                    {...restField}
                    name={[name, 'label']}
                    rules={[{ required: true, message: '' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input placeholder={translateX('label')} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    {...restField}
                    name={[name, 'type']}
                    rules={[{ required: true, message: '' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Select
                      options={COVERAGE_TYPE_OPTIONS}
                      placeholder={translateX('type')}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                    style={{ marginBottom: 8 }}
                  />
                </Col>
              </Row>
            ))}
            <Form.Item style={{ marginBottom: 12 }}>
              <Button
                type="dashed"
                onClick={() => add({ type: 'money' })}
                block
                icon={<PlusOutlined />}
              >
                {translateX('add_coverage')}
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </Form>
    <Row justify="end">
      <Button
        type="primary"
        icon={<SaveOutlined />}
        loading={loading}
        onClick={onSave}
      >
        {translateX('save')}
      </Button>
    </Row>
  </div>
);

const AssistsTabContent = ({ form, loading, onSave }) => (
  <div style={{ padding: '8px 0', overflowY: 'auto', maxHeight: 'calc(80vh - 240px)' }}>
    <Form form={form} layout="vertical">
      <Form.List name="assists">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row gutter={8} key={key} align="middle">
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'key']}
                    rules={[{ required: true, message: '' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input placeholder={translateX('key')} />
                  </Form.Item>
                </Col>
                <Col span={14}>
                  <Form.Item
                    {...restField}
                    name={[name, 'text']}
                    rules={[{ required: true, message: '' }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input placeholder={translateX('description')} />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                    style={{ marginBottom: 8 }}
                  />
                </Col>
              </Row>
            ))}
            <Form.Item style={{ marginBottom: 12 }}>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                {translateX('add_assist')}
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </Form>
    <Row justify="end">
      <Button
        type="primary"
        icon={<SaveOutlined />}
        loading={loading}
        onClick={onSave}
      >
        {translateX('save')}
      </Button>
    </Row>
  </div>
);

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PlansParametersModal;
